/**
 * Year-End Review Calculator
 *
 * 연말정산 데이터 계산 로직
 *
 * === 데이터 정책 ===
 * 1. 상위 카테고리 선정 스레드는 하위 카테고리에서 제외
 *    - Year of the Threads → Notable Threads 제외
 *    - Notable byViews → byReplies 제외 → byReposts 제외
 * 2. Year of the Threads: 조회수 60% + 댓글 20% + 리포스트 20%, Top 3 선정
 * 3. Notable Threads: 카테고리별 Top 3 (상위 선정 스레드 제외)
 * 4. Monthly Report: 월별 그루핑 + 조회수 기준 Top 3
 * 5. Friends of the Year: 상호작용 Top 10 (자기 자신 제외)
 */

/**
 * Year of the Threads 스코어 계산
 * 가중치: 조회수 60% + 댓글 20% + 리포스트 20%
 *
 * @param {object} thread - 스레드 객체
 * @param {array} allThreads - 전체 스레드 배열
 * @returns {number} 종합 점수 (0-1)
 */
export const calculateThreadScore = (thread, allThreads) => {
  if (!thread || !allThreads || allThreads.length === 0) {
    return 0;
  }

  // 1. 조회수 정규화 (0-1)
  const allViews = allThreads.map(t => t.views || 0);
  const maxViews = Math.max(...allViews, 1);
  const viewsScore = (thread.views || 0) / maxViews;

  // 2. 댓글 정규화 (0-1)
  const allReplies = allThreads.map(t => t.replies || 0);
  const maxReplies = Math.max(...allReplies, 1);
  const repliesScore = (thread.replies || 0) / maxReplies;

  // 3. 리포스트 정규화 (0-1)
  const allReposts = allThreads.map(t => t.reposts || 0);
  const maxReposts = Math.max(...allReposts, 1);
  const repostsScore = (thread.reposts || 0) / maxReposts;

  // 4. 종합 점수 (가중치: 조회수 60% + 댓글 20% + 리포스트 20%)
  return (viewsScore * 0.6) + (repliesScore * 0.2) + (repostsScore * 0.2);
};

/**
 * 월별 스레드 그루핑
 *
 * @param {array} threads - 스레드 배열
 * @param {number} year - 연도
 * @returns {object} 월별 데이터 { "01": {...}, "02": {...}, ... }
 */
export const groupThreadsByMonth = (threads, year) => {
  const result = {};

  // 12개월 초기화
  for (let month = 1; month <= 12; month++) {
    const monthKey = String(month).padStart(2, '0');
    result[monthKey] = {
      month: monthKey,
      topThreads: [],
      totalThreads: 0,
      totalViews: 0,
      totalEngagement: 0,
    };
  }

  if (!threads || !Array.isArray(threads)) {
    return result;
  }

  // 해당 연도 스레드만 필터링
  const yearThreads = threads.filter(t => {
    const date = new Date(t.timestamp);
    return date.getFullYear() === year;
  });

  // 월별 통계 집계
  yearThreads.forEach(thread => {
    const date = new Date(thread.timestamp);
    const monthKey = String(date.getMonth() + 1).padStart(2, '0');
    const monthData = result[monthKey];

    if (monthData) {
      monthData.totalThreads++;
      monthData.totalViews += thread.views || 0;
      monthData.totalEngagement +=
        (thread.likes || 0) + (thread.replies || 0) + (thread.reposts || 0);
    }
  });

  // 각 월별 Top 3 계산
  Object.keys(result).forEach(monthKey => {
    const monthThreads = yearThreads.filter(t => {
      const date = new Date(t.timestamp);
      return String(date.getMonth() + 1).padStart(2, '0') === monthKey;
    });

    result[monthKey].topThreads = monthThreads
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 4);
  });

  return result;
};

/**
 * Friends of the Year 계산
 * 상호작용 기준 Top 10 선정 (자신 제외)
 *
 * @param {array} repliesByUser - 사용자별 상호작용 데이터
 * @param {object} ownerInfo - 데이터 소유자 정보 { userId, username } (선택)
 * @returns {array} Top 10 친구 목록 (자신 제외)
 */
export const computeFriendsOfTheYear = (repliesByUser, ownerInfo = null) => {
  if (!Array.isArray(repliesByUser) || repliesByUser.length === 0) {
    return [];
  }

  // 데이터 소유자 ID/username (ownerInfo 우선, 없으면 localStorage fallback)
  const myUserId = String(ownerInfo?.userId || localStorage.getItem('userID') || '');
  const myProfile = ownerInfo ? null : JSON.parse(localStorage.getItem('userProfile') || '{}');
  const myUsername = String(ownerInfo?.username || myProfile?.username || '').toLowerCase();
  const myProfileId = String(ownerInfo?.userId || myProfile?.id || '');

  console.log('🔍 [computeFriendsOfTheYear] Filtering self:', { myUserId, myProfileId, myUsername });
  console.log('🔍 [computeFriendsOfTheYear] Total users before filter:', repliesByUser.length);

  const filtered = repliesByUser
    .filter(user => {
      if (!user || !user.username) return false;

      // 유저 ID를 String으로 정규화하여 비교
      const userIdStr = String(user.userId || user.user_id || user.id || '');
      const usernameStr = String(user.username || '').toLowerCase();

      // '나' 제외 (userId 또는 username으로 비교)
      if (myUserId && userIdStr === myUserId) {
        console.log('🚫 Excluding self by userId:', user.username, userIdStr);
        return false;
      }
      if (myProfileId && userIdStr === myProfileId) {
        console.log('🚫 Excluding self by profileId:', user.username, userIdStr);
        return false;
      }
      if (myUsername && usernameStr === myUsername) {
        console.log('🚫 Excluding self by username:', user.username);
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      const countA = (a.totalInteractions || a.count || 0);
      const countB = (b.totalInteractions || b.count || 0);
      return countB - countA;
    })
    .slice(0, 15)  // 여유있게 15명 (컴포넌트에서 최종 10명 추출)
    .map(user => ({
      userId: user.userId || user.user_id || '',
      username: user.username,
      profileImage: user.profileImage || user.profile_picture_url || '',
      interactionCount: user.totalInteractions || user.count || 0,
      myRepliesCount: user.myRepliesCount || 0,
      theirRepliesCount: user.theirCommentsCount || user.theirRepliesCount || 0,
      // conversations 원본 전체 유지 (threadId로 그루핑은 ConversationView에서 처리)
      conversations: user.conversations || [],
    }));

  console.log('✅ [computeFriendsOfTheYear] Final friends count:', filtered.length);
  return filtered;
};

/**
 * 연말 통계 계산
 *
 * @param {array} yearThreads - 해당 연도 스레드 배열
 * @param {object} monthlyReport - 월별 리포트 데이터
 * @returns {object} 통계 객체
 */
const computeYearStats = (yearThreads, monthlyReport) => {
  if (!yearThreads || yearThreads.length === 0) {
    return {
      totalThreads: 0,
      totalViews: 0,
      totalLikes: 0,
      totalReplies: 0,
      totalReposts: 0,
      mostActiveMonth: null,
      averageViewsPerThread: 0,
      averageEngagementRate: 0,
    };
  }

  const totalViews = yearThreads.reduce((sum, t) => sum + (t.views || 0), 0);
  const totalLikes = yearThreads.reduce((sum, t) => sum + (t.likes || 0), 0);
  const totalReplies = yearThreads.reduce((sum, t) => sum + (t.replies || 0), 0);
  const totalReposts = yearThreads.reduce((sum, t) => sum + (t.reposts || 0), 0);

  // 가장 활발한 월 찾기
  const monthCounts = Object.entries(monthlyReport)
    .map(([month, data]) => ({ month, count: data.totalThreads }))
    .sort((a, b) => b.count - a.count);

  const totalEngagement = totalLikes + totalReplies + totalReposts;

  return {
    totalThreads: yearThreads.length,
    totalViews,
    totalLikes,
    totalReplies,
    totalReposts,
    mostActiveMonth: monthCounts[0]?.count > 0 ? monthCounts[0].month : null,
    averageViewsPerThread: Math.round(totalViews / yearThreads.length),
    averageEngagementRate: totalViews > 0
      ? ((totalEngagement / totalViews) * 100).toFixed(2)
      : 0,
  };
};

/**
 * Year of the Threads 선정 (Top 3)
 *
 * 선정 조건:
 * - 최소 조회수: 100회 이상
 * - 최소 참여: likes + replies + reposts >= 5
 * - 조건 미충족 시 조회수 상위로 대체
 * - 가중치: 조회수 60% + 댓글 20% + 리포스트 20%
 *
 * @param {array} yearThreads - 해당 연도 스레드 배열
 * @returns {object|null} Year of the Threads 데이터 { threads: [], threadIds: [] }
 */
const selectYearOfTheThreads = (yearThreads) => {
  if (!yearThreads || yearThreads.length === 0) {
    return null;
  }

  // 자격 조건: 조회수 100 이상 + 참여 5 이상
  const eligibleThreads = yearThreads.filter(t => {
    const views = t.views || 0;
    const engagement = (t.likes || 0) + (t.replies || 0) + (t.reposts || 0);
    return views >= 100 && engagement >= 5;
  });

  let topThreads = [];

  if (eligibleThreads.length > 0) {
    // 스코어링으로 Top 3 선정
    const scored = eligibleThreads.map(t => ({
      thread: t,
      score: calculateThreadScore(t, yearThreads),
    }));

    topThreads = scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(item => {
        const thread = item.thread;
        const engagement = (thread.likes || 0) + (thread.replies || 0) + (thread.reposts || 0);
        const engagementRate = (engagement / Math.max(thread.views || 1, 1)) * 100;

        // 순위 계산
        const sortedByViews = [...yearThreads].sort((a, b) => (b.views || 0) - (a.views || 0));
        const viewsRank = sortedByViews.findIndex(t => t.id === thread.id) + 1;

        return {
          thread,
          score: item.score,
          viewsRank,
          engagementRate: parseFloat(engagementRate.toFixed(2)),
        };
      });
  } else if (yearThreads.length > 0) {
    // 조건 미충족 시 조회수 상위 3개로 대체
    const sortedByViews = [...yearThreads].sort((a, b) => (b.views || 0) - (a.views || 0));
    topThreads = sortedByViews.slice(0, 3).map((thread, index) => {
      const engagement = (thread.likes || 0) + (thread.replies || 0) + (thread.reposts || 0);
      const engagementRate = (engagement / Math.max(thread.views || 1, 1)) * 100;

      return {
        thread,
        score: 1,
        viewsRank: index + 1,
        engagementRate: parseFloat(engagementRate.toFixed(2)),
      };
    });
  }

  if (topThreads.length === 0) {
    return null;
  }

  return {
    threads: topThreads,
    threadIds: topThreads.map(item => item.thread.id),
  };
};

/**
 * Notable Threads 선정
 * 카테고리별 Top 3 (상위 카테고리 선정 스레드 cascade 제외)
 *
 * 제외 정책:
 * 1. Year of the Threads 선정 스레드 제외
 * 2. byViews 선정 → byReplies에서 제외
 * 3. byViews + byReplies 선정 → byReposts에서 제외
 *
 * @param {array} yearThreads - 해당 연도 스레드 배열
 * @param {array} excludeThreadIds - 제외할 스레드 ID 배열 (Year of the Threads 선정 스레드)
 * @returns {object} { byViews: [], byReplies: [], byReposts: [] }
 */
const selectNotableThreads = (yearThreads, excludeThreadIds = []) => {
  if (!yearThreads || yearThreads.length === 0) {
    return {
      byViews: [],
      byReplies: [],
      byReposts: [],
    };
  }

  // Year of the Threads 선정 스레드 제외
  const excludeSet = new Set(excludeThreadIds);
  const baseFilteredThreads = yearThreads.filter(t => !excludeSet.has(t.id));

  // 1. byViews 선정 (Year of the Threads 제외)
  const byViews = [...baseFilteredThreads]
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 3);

  // 2. byReplies 선정 (Year of the Threads + byViews 제외)
  const byViewsIds = new Set(byViews.map(t => t.id));
  const afterViewsFiltered = baseFilteredThreads.filter(t => !byViewsIds.has(t.id));
  const byReplies = [...afterViewsFiltered]
    .sort((a, b) => (b.replies || 0) - (a.replies || 0))
    .slice(0, 3);

  // 3. byReposts 선정 (Year of the Threads + byViews + byReplies 제외)
  const byRepliesIds = new Set(byReplies.map(t => t.id));
  const afterRepliesFiltered = afterViewsFiltered.filter(t => !byRepliesIds.has(t.id));
  const byReposts = [...afterRepliesFiltered]
    .sort((a, b) => (b.reposts || 0) - (a.reposts || 0))
    .slice(0, 3);

  return { byViews, byReplies, byReposts };
};

/**
 * 연말정산 전체 요약 계산
 *
 * @param {array} threads - 전체 스레드 배열
 * @param {number} year - 연도 (예: 2024)
 * @returns {object} YearEndSummary 객체
 */
export const computeYearEndSummary = (threads, year) => {
  // 해당 연도 스레드만 필터링
  const yearThreads = (threads || []).filter(t => {
    if (!t.timestamp) return false;
    const date = new Date(t.timestamp);
    return date.getFullYear() === year;
  });

  // Monthly Report
  const monthlyReport = groupThreadsByMonth(yearThreads, year);

  // 데이터 없음 처리
  if (yearThreads.length === 0) {
    return {
      year,
      computedAt: new Date().toISOString(),
      yearOfTheThreads: null,
      notableThreads: { byViews: [], byReplies: [], byReposts: [] },
      monthlyReport,
      friendsOfTheYear: [], // Friends는 별도 로딩 후 병합
      stats: computeYearStats([], monthlyReport),
    };
  }

  // Year of the Threads (Top 3)
  const yearOfTheThreads = selectYearOfTheThreads(yearThreads);

  // Notable Threads (Year of the Threads 선정 스레드 제외)
  const excludeThreadIds = yearOfTheThreads?.threadIds || [];
  const notableThreads = selectNotableThreads(yearThreads, excludeThreadIds);

  // Stats
  const stats = computeYearStats(yearThreads, monthlyReport);

  return {
    year,
    computedAt: new Date().toISOString(),
    yearOfTheThreads,
    notableThreads,
    monthlyReport,
    friendsOfTheYear: [], // Friends는 별도 로딩 후 병합
    stats,
  };
};

/**
 * Friends 데이터 병합
 *
 * @param {object} summary - 기존 YearEndSummary
 * @param {array} repliesByUser - 사용자별 상호작용 데이터
 * @returns {object} Friends 포함 YearEndSummary
 */
export const mergeFriendsData = (summary, repliesByUser) => {
  if (!summary) {
    return null;
  }

  return {
    ...summary,
    friendsOfTheYear: computeFriendsOfTheYear(repliesByUser),
  };
};
